import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import CtaLink from '@/components/CtaLink';
import { useLanguage } from '@/components/LanguageContext';
import { withCta } from '@/lib/ctaTracking';
import { urlDe } from '@/lib/rutasPublicas';
import LandingShellV3 from '@/components/landing/v3/LandingShellV3';
import CtaFinalOscuro from '@/components/landing/v3/CtaFinalOscuro';
import FaqAcordeon from '@/components/landing/v3/FaqAcordeon';
import SectionKicker from '@/components/landing/v3/SectionKicker';
import { pickLandingV3Content } from '@/pages/landingV3.content';

/**
 * SectorV3 — UNA página para los cuatro sectores de Soluciones.
 *
 * `Restauración`, `Clínicas`, `Automotriz` y `Hoteles` son cuatro `.dc.html`
 * distintos en el diseño, pero se leyeron los cuatro enteros y comparten la
 * MISMA plantilla de siete secciones, sección a sección. Lo único que cambia es
 * el copy, los cuatro locales del hero y el widget del bloque «Por qué
 * importa».
 *
 * Por eso aquí hay un componente y no cuatro: cuatro copias de la misma
 * plantilla divergen al primer retoque de maquetación, y son cuatro sitios
 * donde arreglar cada fallo. El copy vive en `landingV3.sectores.js`.
 *
 * `clave` la fija la ruta en `App.jsx`, no la URL: así renombrar un path no
 * rompe el copy.
 *
 * **Los CTAs sí llevan el sector dentro del id** (`sector.hoteles.hero.trial`).
 * Es lo contrario que el resto del componente, y a propósito: la pregunta que
 * hay que poder responder es qué sector convierte, y un id común para las
 * cuatro las fundiría en un solo embudo.
 */
export const SECTORES = ['restauracion', 'clinicas', 'automotriz', 'hoteles'];

/**
 * Los cuatro CTAs de cada sector, ESCRITOS ENTEROS.
 *
 * Se podrían componer (``sector.${clave}.hero.trial``) y serían cuatro
 * líneas en vez de dieciséis. No se hace porque la guarda de CI solo encuentra
 * ids LITERALES: con la plantilla, las dieciséis entradas del registro saldrían
 * como «declaradas y sin usar» y el test fallaría. Y, ya puestos, escritos se
 * pueden buscar con un grep, que es lo que uno hace cuando ve un id raro en el
 * panel de CTAs.
 */
const CTAS = {
  restauracion: {
    ctaHeroTrial: 'sector.restauracion.hero.trial',
    ctaHeroMulti: 'sector.restauracion.hero.multi',
    ctaCierreTrial: 'sector.restauracion.cierre.trial',
    ctaCierreVentas: 'sector.restauracion.cierre.ventas',
  },
  clinicas: {
    ctaHeroTrial: 'sector.clinicas.hero.trial',
    ctaHeroMulti: 'sector.clinicas.hero.multi',
    ctaCierreTrial: 'sector.clinicas.cierre.trial',
    ctaCierreVentas: 'sector.clinicas.cierre.ventas',
  },
  automotriz: {
    ctaHeroTrial: 'sector.automotriz.hero.trial',
    ctaHeroMulti: 'sector.automotriz.hero.multi',
    ctaCierreTrial: 'sector.automotriz.cierre.trial',
    ctaCierreVentas: 'sector.automotriz.cierre.ventas',
  },
  hoteles: {
    ctaHeroTrial: 'sector.hoteles.hero.trial',
    ctaHeroMulti: 'sector.hoteles.hero.multi',
    ctaCierreTrial: 'sector.hoteles.cierre.trial',
    ctaCierreVentas: 'sector.hoteles.cierre.ventas',
  },
};

const TONOS = {
  bien: 'bg-v3-greensoft text-v3-green',
  medio: 'bg-v3-goldsoft text-v3-golddeep',
  mal: 'bg-v3-redsoft text-v3-red',
};

const AVATARES = {
  bien: { bg: '#dcfce7', fg: '#15803d' },
  medio: { bg: '#fdf1dc', fg: '#e69a1e' },
  mal: { bg: '#fee2e2', fg: '#b91c1c' },
};

export default function SectorV3({ clave }) {
  const { language } = useLanguage();
  const raiz = pickLandingV3Content(language);
  const c = raiz.sectores[clave];
  const comun = raiz.sectores.comun;
  const ctas = CTAS[clave];

  // Una `clave` que no existe sería una ruta mal escrita en `App.jsx`. Mejor no
  // montar nada que reventar con un `undefined.hero` tres componentes más
  // abajo, donde el error no dice de dónde viene.
  if (!c || !ctas) return null;

  return (
    <LandingShellV3>
      <Hero c={c.hero} ctas={ctas} idioma={language} />
      <Problema c={c.problema} />
      {/* `idioma` baja hasta aquí porque las tarjetas enlazan a las páginas de
          Producto, y cada idioma tiene las suyas. */}
      <Razones c={c.razones} comun={comun} idioma={language} />
      <Panel c={c.panel} comun={comun} idioma={language} />
      <PorQue c={c.porQue} kicker={comun.kickerPorQue} />

      <FaqAcordeon titulo={comun.faqTitulo} preguntas={c.faq} />

      <CtaFinalOscuro
        tituloA={c.cierre.titulo}
        acento={c.cierre.acento}
        subtitulo={c.cierre.sub}
        cta={comun.cierreCta}
        microcopy={comun.cierreMicrocopy}
        enterpriseA={comun.cierreEnterpriseA}
        enterpriseCta={comun.cierreEnterpriseCta}
        ctaTrial={ctas.ctaCierreTrial}
        ctaVentas={ctas.ctaCierreVentas}
      />
    </LandingShellV3>
  );
}

function Hero({ c, ctas, idioma }) {
  const idTrial = ctas.ctaHeroTrial;
  const idMulti = ctas.ctaHeroMulti;

  return (
    <section className="bg-white px-6 pb-6">
      <div className="max-w-[1180px] mx-auto flex flex-wrap items-center gap-8 lg:gap-12 pt-4 pb-8">
        <div className="flex-1 basis-[420px] min-w-0">
          <div className="inline-flex items-center bg-v3-surface border border-v3-line rounded-full px-[15px] py-2 text-[13px] font-medium text-v3-body">
            {c.badge}
          </div>

          {/* El diseño fija 50 px; a 375 px eso deja tres palabras por línea. */}
          <h1 className="text-[34px] sm:text-[42px] lg:text-[50px] leading-[1.06] tracking-[-.035em] font-bold text-v3-ink mt-5 max-w-[20ch]">
            {c.titulo}
            <span className="text-v3-golddeep">{c.acento}</span>
          </h1>

          <p className="text-[17px] lg:text-[18px] leading-[1.6] text-v3-body mt-[18px] max-w-[52ch]">
            {c.sub}
          </p>

          <div className="flex gap-3 flex-wrap mt-7">
            <CtaLink
              as={Link}
              cta={idTrial}
              to={withCta('/register', idTrial)}
              className="inline-flex items-center bg-v3-gold hover:bg-v3-golddeep text-v3-ink text-base font-semibold rounded-full px-[26px] py-[15px] whitespace-nowrap transition-colors"
            >
              {c.cta}
            </CtaLink>
            <CtaLink
              as={Link}
              cta={idMulti}
              to={urlDe('multiubicaciones', idioma)}
              className="inline-flex items-center gap-2 bg-white border border-v3-line hover:border-v3-faint text-v3-ink text-base font-semibold rounded-full px-[25px] py-[14px] transition-colors"
            >
              {c.ctaMulti}
              <ArrowRight className="w-4 h-4 flex-none" aria-hidden="true" />
            </CtaLink>
          </div>
        </div>

        <div className="flex-1 basis-[380px] min-w-0 w-full">
          <ListaLocales c={c} />
        </div>
      </div>
    </section>
  );
}

/**
 * El mock de «tus locales»: cuatro sedes con su nivel y su puntuación.
 *
 * Los cuatro niveles son los reales de la app (`healthScore.level.*`): Líder
 * local, Bien posicionado, Camino al podio y Empezando. Y el ranking que imita
 * existe de verdad, así que quien se registre reconocerá la pantalla.
 */
function ListaLocales({ c }) {
  return (
    <div className="bg-white border border-v3-line rounded-[20px] v3-shadow p-4 max-w-[400px] mx-auto">
      <div className="flex items-center justify-between gap-2.5 px-1 pb-3.5">
        <span className="text-[13.5px] font-bold tracking-[-.01em] text-v3-ink">
          {c.listaTitulo}
        </span>
        <span className="text-[11.5px] font-semibold bg-v3-sunken text-v3-body rounded-full px-2.5 py-1.5 whitespace-nowrap">
          {c.listaEtiqueta}
        </span>
      </div>

      <ul className="bg-v3-surface rounded-2xl overflow-hidden">
        {c.locales.map((l) => (
          <li
            key={l.clave}
            className="border-t border-black/5 first:border-t-0 px-3.5 py-3 flex items-center gap-3"
          >
            <span
              aria-hidden="true"
              className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-xs font-bold flex-none"
              style={{ background: AVATARES[l.tono].bg, color: AVATARES[l.tono].fg }}
            >
              {l.i}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[13.5px] font-bold tracking-[-.01em] text-v3-ink truncate">
                {l.n}
              </span>
              <span className="block text-[11.5px] text-v3-muted mt-0.5">{l.nivel}</span>
            </span>
            <span
              className={`flex-none text-[13px] font-bold rounded-full px-2.5 py-1.5 ${TONOS[l.tono]}`}
            >
              {l.p}
            </span>
          </li>
        ))}
      </ul>

      <p className="flex items-center gap-2.5 px-1 pt-3.5 text-[12.5px] text-v3-body">
        <span className="w-2 h-2 rounded-full bg-v3-red flex-none" aria-hidden="true" />
        {c.aviso}
      </p>
    </div>
  );
}

function Problema({ c }) {
  return (
    <section className="px-6 pt-[60px]">
      <div className="max-w-[1180px] mx-auto bg-v3-surface rounded-3xl p-7 sm:p-10">
        <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[30ch]">
          {c.titulo}
        </h2>
        <p className="text-base leading-[1.7] text-v3-body mt-4 max-w-[70ch]">{c.p1}</p>
        <p className="text-base leading-[1.7] text-v3-body mt-3.5 max-w-[70ch]">{c.p2}</p>
      </div>
    </section>
  );
}

function Razones({ c, comun, idioma }) {
  return (
    <section className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto">
        <SectionKicker>{c.kicker}</SectionKicker>

        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 mt-7">
          {c.items.map((r) => (
            <li key={r.clave}>
              {/* Enlace interno entre páginas de marketing, no un CTA de
                  captación: no va al registro ni al pago, así que no se declara
                  en el registro de CTAs (mismo criterio que los items del
                  desplegable de la cabecera). */}
              <Link
                to={urlDe(r.clave, idioma)}
                className="group block h-full bg-white border border-v3-line hover:border-v3-gold rounded-[20px] px-6 py-[26px] transition-colors"
              >
                <span className="inline-flex text-[12.5px] font-bold text-v3-golddeep bg-v3-goldsoft rounded-full px-2.5 py-1.5">
                  {r.t}
                </span>
                <p className="text-[15.5px] leading-[1.6] text-v3-body mt-3.5">{r.d}</p>
                <span className="inline-flex items-center gap-1.5 text-[13.5px] font-bold text-v3-ink mt-4">
                  {comun.verProducto} {r.t}
                  <ArrowRight
                    className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Panel({ c, comun, idioma }) {
  return (
    <section className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto bg-v3-surface rounded-3xl p-7 sm:p-10">
        <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[24ch]">
          {c.titulo}
        </h2>
        <p className="text-base leading-[1.7] text-v3-body mt-4 max-w-[68ch]">{c.cuerpo}</p>
        <Link
          to={urlDe('multiubicaciones', idioma)}
          className="inline-flex items-center gap-1.5 text-[15px] font-bold text-v3-ink border-b-2 border-v3-gold pb-0.5 mt-5 hover:text-v3-golddeep transition-colors"
        >
          {comun.panelEnlace}
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

/**
 * «Por qué importa». El widget de la izquierda es lo único que de verdad
 * distingue a un sector de otro, y son cuatro cosas distintas, así que se elige
 * por `c.widget` en vez de intentar un componente genérico que acabaría con
 * cuatro ramas dentro igualmente.
 */
function PorQue({ c, kicker }) {
  return (
    <section className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto">
        <SectionKicker>{kicker}</SectionKicker>

        <div className="bg-v3-surface rounded-3xl p-7 sm:p-10 mt-7 flex flex-wrap gap-8 lg:gap-11 items-center">
          <div className="flex-none w-full sm:w-[210px]">
            <Widget c={c} />
            <p className="text-[13px] text-v3-muted mt-3">{c.pie}</p>
          </div>

          <div className="flex-1 min-w-[280px]">
            <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[26ch]">
              {c.titulo}
            </h2>
            <p className="text-base leading-[1.7] text-v3-body mt-4 max-w-[62ch]">{c.cuerpo}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Widget({ c }) {
  // Los cuatro son ilustraciones: lo que cuentan ya está en la prosa de al
  // lado, así que ninguno aporta nada leído en voz alta.
  if (c.widget === 'podio') {
    return (
      <div aria-hidden="true" className="flex flex-col gap-2">
        {[1, 2, 3, 4].map((n) => {
          const dentro = n <= 3;
          return (
            <div
              key={n}
              className={`rounded-xl px-3.5 py-2.5 flex items-center gap-2.5 ${
                dentro ? 'bg-white' : ''
              }`}
            >
              <span
                className={`text-[13px] font-bold w-3.5 flex-none ${
                  dentro ? 'text-v3-ink' : 'text-v3-faint'
                }`}
              >
                {n}
              </span>
              <span
                className={`flex-1 h-1.5 rounded-full block ${
                  dentro ? 'bg-v3-gold' : 'bg-v3-track'
                }`}
              />
            </div>
          );
        })}
      </div>
    );
  }

  if (c.widget === 'idiomas') {
    return (
      <ul aria-hidden="true" className="flex flex-col gap-2">
        {c.items.map((i) => (
          <li
            key={i.clave}
            className="bg-white border border-black/5 rounded-xl px-3.5 py-2.5 flex items-center gap-2.5"
          >
            <span className="text-[11px] font-bold text-v3-golddeep bg-v3-goldsoft rounded-md px-1.5 py-1 flex-none">
              {i.k}
            </span>
            <span className="flex-1 text-[12.5px] text-v3-body truncate">{i.t}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (c.widget === 'comparativa') {
    return (
      <ul aria-hidden="true" className="flex flex-col gap-2">
        {c.items.map((i) => (
          <li
            key={i.clave}
            className={`rounded-xl px-3.5 py-2.5 flex items-center gap-2.5 ${
              i.destacada
                ? 'bg-white border-2 border-v3-gold'
                : `border border-black/5 ${i.atenuada ? '' : 'bg-white'}`
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full flex-none ${
                i.destacada ? 'bg-v3-gold' : 'bg-v3-faint'
              }`}
            />
            <span
              className={`flex-1 text-[13px] ${
                i.destacada
                  ? 'font-bold text-v3-ink'
                  : `font-medium ${i.atenuada ? 'text-v3-faint' : 'text-v3-body'}`
              }`}
            >
              {i.n}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  // balanza
  return (
    <div aria-hidden="true" className="flex flex-col gap-2">
      {c.items.map((b) => (
        <div
          key={b.clave}
          className={`bg-white rounded-2xl px-4 py-3.5 ${
            b.destacada ? 'border-2 border-v3-gold' : 'border border-black/5'
          }`}
        >
          <div
            className={`text-xs font-bold ${b.destacada ? 'text-v3-ink' : 'text-v3-muted'}`}
          >
            {b.l}
          </div>
          <div className="h-[7px] rounded-full bg-v3-track mt-2.5 overflow-hidden">
            <div
              className={`h-full rounded-full ${b.destacada ? 'bg-v3-gold' : 'bg-v3-faint'}`}
              style={{ width: `${b.pct}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
